import type { DeviceModel } from "@irriga-mais/common";
import clsx from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { ChevronRight, CircleQuestionMark, Clock, Droplets, Play, Sprout, Square, Timer } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { useLocation } from "wouter";
import type { DeviceModelId } from "@/api/model/device";
import { WATER_PUMP_TYPE_LABELS, type WaterPumpType } from "@/api/model/water-pump";
import type { AUTProject, AUTSector } from "@/api/queries/account";
import { useToast } from "@/components";
import { DeviceActivationPanel } from "@/components/DeviceActivationPanel";
import Modal from "@/components/Modal";
import Button from "@/components/ui/Button";
import { appShell, projectsAtom, propertyDevicesAtom, waterPumpsAtom } from "@/store";
import { createDeviceMessageRequestAtom } from "@/store/crud";
import {
  deviceToLicMapAtom,
  equipmentsStateAtom,
  sectorsByProjectIdAtom,
  sectorValveStatusMapAtom,
  sectorVirtualDeviceConfigMapAtom,
  selectedPropertyAtom,
  waterPumpVirtualDeviceConfigMapAtom,
} from "@/store/data";
import { dayjs } from "@/utils/date";
import { findWaterPumpAssociations } from "@/utils/device-associations";
import {
  type ExtendedDeviceDisplayInfo,
  formatDeviceDisplayText,
  formatLICDisplayText,
  resolveDeviceDisplayInfo,
} from "@/utils/device-label-utils";
import { getDeviceModelCommercialName } from "@/utils/device-model";
import { isLastOpenValveForRunningPump } from "@/utils/valve-control";

interface ConsolidatedWaterPump {
  id: string;
  metadata: any;
  notes: string | null;
  label: string;
  identifier: string;
  pump_type: WaterPumpType;
  pump_model: string;
  water_pump_controller:
    | {
        id: string;
        metadata: any;
        notes: string | null;
        identifier: string;
        model: DeviceModel;
      }
    | undefined;
  water_pump_controller_display_info: ExtendedDeviceDisplayInfo | null;
  current_state: {
    status: "on" | "off" | null;
    time_left_minutes: number | null;
    start_time: string | null;
  };
  last_activity: string;
}

/** Check valve closure against all projects served by the currently running pumps. */
function useLastOpenValveCheck(pumps: readonly ConsolidatedWaterPump[]) {
  const projects = useAtomValue(projectsAtom);
  const valveStatuses = useAtomValue(sectorValveStatusMapAtom);
  const sectorConfigs = useAtomValue(sectorVirtualDeviceConfigMapAtom);
  const openSectorIds = new Set([...valveStatuses].filter(([, status]) => status === "on").map(([id]) => id));
  const runningPumpSectorGroups = pumps
    .filter((pump) => pump.current_state.status === "on")
    .map(
      (pump) =>
        new Set(
          projects
            .filter(
              (project) => project.irrigation_water_pump === pump.id || project.fertigation_water_pump === pump.id
            )
            .flatMap((project) => project.sectors)
            .filter((sector) => sectorConfigs.has(sector.id))
            .map((sector) => sector.id)
        )
    );

  return (sectorId: string) => isLastOpenValveForRunningPump(sectorId, runningPumpSectorGroups, openSectorIds);
}

// function genRandomDate(maxDaysAgo: number): string {
//   const now = new Date();
//   const randomDaysAgo = Math.floor(
//     Math.random() * maxDaysAgo * 24 * 60 * 60 * 1000
//   );
//   const randomDate = new Date(now.getTime() - randomDaysAgo);
//   return randomDate.toISOString();
// }

function genRandomPumpState(): {
  status: "on" | "off";
  time_left_minutes: number | null;
  start_time: string | null;
} {
  const status = "off";
  return {
    status,
    time_left_minutes: null,
    start_time: null,
  };
}

interface WaterPumpControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  pump: ConsolidatedWaterPump;
  onAction: (action: "turn_on" | "turn_off", duration?: number) => void;
  projects?: AUTProject[];
  hideSectors?: boolean;
}

interface SectorsWithProjectsProps {
  sector: AUTSector;
  project: {
    id: string;
    name: string;
  };
}

function WaterPumpControlModal({ isOpen, onClose, pump, onAction, projects, hideSectors }: WaterPumpControlModalProps) {
  const sectorsByProjectId = useAtomValue(sectorsByProjectIdAtom);
  const durationId = useId();
  const [duration, setDuration] = useState<string>("30");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedProperty = useAtomValue(selectedPropertyAtom);
  const associations = findWaterPumpAssociations(pump.id, selectedProperty);
  const sectorVirtualDeviceConfigMap = useAtomValue(sectorVirtualDeviceConfigMapAtom);

  const associatedProjectIds = useMemo(() => {
    return associations.map((a) => a.id);
  }, [associations]);

  const associatedProjects = useMemo(() => {
    if (!projects?.length || !associatedProjectIds.length) return [];

    return projects.filter((project: AUTProject) => associatedProjectIds.includes(project.id));
  }, [projects, associatedProjectIds]);

  const sectorsWithProject = useMemo(() => {
    if (!associatedProjects.length) return [];

    return associatedProjects.flatMap((project: AUTProject) => {
      const sectors = sectorsByProjectId(project.id) || [];

      return sectors.map((sector) => ({
        sector,
        project: {
          id: project.id,
          name: project.name,
        },
      }));
    });
  }, [associatedProjects, sectorsByProjectId]);

  const [pendingTurnOff, setPendingTurnOff] = useState<null | (() => void)>(null);

  const valveStatusMap = useAtomValue(sectorValveStatusMapAtom);
  const requiresValveCloseConfirmation = useLastOpenValveCheck([pump]);

  const openValvesCount = useMemo(() => {
    return sectorsWithProject.filter((sectorProject: SectorsWithProjectsProps) => {
      const hasController = sectorVirtualDeviceConfigMap.has(sectorProject.sector.id);
      const isOpen = hasController && valveStatusMap.get(sectorProject.sector.id) === "on";
      return isOpen;
    }).length;
  }, [sectorsWithProject, valveStatusMap, sectorVirtualDeviceConfigMap]);

  const hasOpenValve = openValvesCount > 0;
  const [warningModal, setWarningModal] = useState<{
    isOpen: boolean;
    type: "rule1" | "rule2" | "rule3" | "";
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });

  const isRunning = pump.current_state.status === "on";

  const handleOpenWarningModal = () => {
    setWarningModal((prev) => ({ ...prev, isOpen: true }));
  };

  const handleCloseWarningModal = () => {
    setWarningModal((prev) => ({ ...prev, isOpen: false }));
  };

  /** Confirm before closing the last open valve serving this running pump. */
  const handleCloseValve = (sectorId: string, turnOffFn: () => void) => {
    if (requiresValveCloseConfirmation(sectorId)) {
      setPendingTurnOff(() => turnOffFn);
      setWarningModal({
        isOpen: true,
        type: "rule2",
        title: "Atenção – Uma válvula aberta",
        message: "Fechar a válvula com a bomba ligada pode causar danos à tubulação. Tem certeza que deseja continuar?",
      });
      handleOpenWarningModal();
      return;
    }
    turnOffFn();
  };

  const handleTurnOn = async () => {
    const durationNum = parseInt(duration);
    if (isNaN(durationNum) || durationNum <= 0) {
      return;
    }
    // REGRA 1: ligar bomba sem válvula aberta
    if (!hasOpenValve && sectorsWithProject.length > 0) {
      setWarningModal({
        isOpen: true,
        type: "rule1",
        title: "Atenção – Nenhuma válvula aberta",
        message:
          "Não há válvulas abertas. Ligar a bomba pode danificar a tubulação ou a bomba. Verifique as válvulas antes de continuar.",
      });
      handleOpenWarningModal();
      return;
    }

    setIsSubmitting(true);
    try {
      await onAction("turn_on", durationNum);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTurnOff = async () => {
    setIsSubmitting(true);
    try {
      await onAction("turn_off");
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTimeLeft = (minutes: number | null) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Acionamento da Bomba" size="md">
      <div className="space-y-2">
        {/* Pump Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-2">{pump.label}</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Status:</span>
              <span className={`ml-2 font-medium ${isRunning ? "text-green-600" : "text-gray-600"}`}>
                {isRunning ? "Ligada" : "Desligada"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Tipo:</span>
              <span className="ml-2 text-gray-900">{WATER_PUMP_TYPE_LABELS[pump.pump_type] || pump.pump_type}</span>
            </div>
            {isRunning && pump.current_state.time_left_minutes && (
              <div className="col-span-2">
                <span className="text-gray-500">Tempo Restante:</span>
                <span className="ml-2 font-medium text-green-600">
                  {formatTimeLeft(pump.current_state.time_left_minutes)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Controller Info */}
        <div className={`${pump.water_pump_controller ? "bg-blue-50" : "bg-red-50"} p-2 flex shadow gap-1`}>
          <div className="text-gray-600 text-xs font-medium ">Controlador de Irrigação:</div>
          <div className="text-xs grow">
            <span
              className={clsx("font-normal", {
                "text-gray-900": pump.water_pump_controller,
                "text-red-600": !pump.water_pump_controller,
              })}
            >
              {pump.water_pump_controller_display_info
                ? formatDeviceDisplayText(pump.water_pump_controller_display_info)
                : pump.water_pump_controller
                  ? getDeviceModelCommercialName(pump.water_pump_controller.model)
                  : "Nenhum controlador associado"}
            </span>
          </div>
        </div>

        {/* Turn On Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Play className="w-4 h-4 text-green-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">Ligar Bomba</h4>
              <p className="text-sm text-gray-600">Defina o tempo de funcionamento em minutos</p>
            </div>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                Duração (minutos)
              </label>
              <div className="relative">
                <Timer className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  id={durationId}
                  type="number"
                  min="1"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="30"
                />
              </div>
            </div>
            <button
              onClick={handleTurnOn}
              disabled={isSubmitting || !duration || parseInt(duration) <= 0 || pump.water_pump_controller == null}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? "Ligando..." : "Ligar"}
            </button>
          </div>

          {/* <div className="text-xs text-gray-500">
            Tempo máximo: 8 horas (480 minutos)
          </div> */}
        </div>

        {/* Turn Off Section */}
        <div className="border-t pt-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <Square className="w-4 h-4 text-red-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">Desligar Bomba</h4>
              <p className="text-sm text-gray-600">Interrompe o funcionamento imediatamente</p>
            </div>
            <button
              onClick={handleTurnOff}
              disabled={isSubmitting || pump.water_pump_controller == null}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? "Desligando..." : "Desligar"}
            </button>
          </div>
        </div>

        {/* Sectors Acionated By This Bomb */}
        {!hideSectors && (
          <>
            <div className="my-6 border-t border-gray-200" />
            {sectorsWithProject.length > 0 ? (
              <div className="mt-4">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Controle de Setores</h4>
                  <p className="text-sm text-gray-600">
                    É necessário que ao menos um setor esteja ligado para o acionamento da bomba com segurança.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {sectorsWithProject.map((sectorProject: SectorsWithProjectsProps) => {
                    const valveStatus: "on" | "off" = valveStatusMap.get(sectorProject.sector.id) || "off";
                    const controllerId = sectorVirtualDeviceConfigMap.get(sectorProject.sector.id)?.device?.id;

                    return (
                      controllerId && (
                        <DeviceActivationPanel
                          key={sectorProject.sector.id}
                          elementType="valve"
                          elementId={sectorProject.sector?.id}
                          elementControllerId={controllerId}
                          turnOnDurationMinutes={1}
                          title={`${sectorProject.sector.name}`}
                          status={valveStatus}
                          onTurnOffRequest={(turnOff) => handleCloseValve(sectorProject.sector.id, turnOff)}
                          remainingPumpTimeMinutes={pump.current_state.time_left_minutes}
                        />
                      )
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <h5 className="font-medium text-yellow-800">Nenhum setor associado</h5>
                <p className="text-sm text-yellow-700">
                  Esta bomba não possui setores vinculados no sistema. Caso esta bomba opere de forma independente ou em
                  outro contexto, nenhuma ação adicional é necessária.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <Modal isOpen={warningModal.isOpen} onClose={handleCloseWarningModal} title={warningModal.title} size="sm">
        <div className="space-y-4">
          <div>
            <label htmlFor={durationId} className="block text-sm font-medium text-gray-700 mb-1">
              {warningModal.message}
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleCloseWarningModal}>
              Cancelar
            </Button>
            {warningModal.type === "rule1" && (
              <Button
                variant="warning"
                onClick={async () => {
                  const durationNum = parseInt(duration);
                  if (isNaN(durationNum) || durationNum <= 0) {
                    return;
                  }
                  setIsSubmitting(true);
                  try {
                    await onAction("turn_on", durationNum);
                    onClose();
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
              >
                Ligar
              </Button>
            )}
            {warningModal.type === "rule2" && (
              <Button
                variant="warning"
                onClick={async () => {
                  pendingTurnOff?.();
                  setPendingTurnOff(null);
                  handleCloseWarningModal();
                }}
              >
                Desligar
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </Modal>
  );
}

/** Data for a project with its associated pumps */
interface ProjectPumpGroupData {
  project: AUTProject;
  pumps: ConsolidatedWaterPump[];
}

interface ProjectCardProps {
  group: ProjectPumpGroupData;
  onClick: () => void;
}

/** Open a project's controls with a pointer or the keyboard. */
function ProjectCard({ group, onClick }: ProjectCardProps) {
  const { project, pumps } = group;
  const hasIrrigation = !!project.irrigation_water_pump;
  const hasFertigation = !!project.fertigation_water_pump;

  return (
    <article className="relative bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
      <button
        type="button"
        aria-label={`Abrir controles do projeto ${project.name}`}
        className="absolute inset-0 z-10 w-full rounded-lg cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
        onClick={onClick}
      />
      <div className="p-4">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Sprout className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{project.name}</h3>
              {project.description && <p className="text-xs text-gray-500 line-clamp-1">{project.description}</p>}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div>
            <p className="text-gray-500 mb-1">Bombas</p>
            <p className="text-gray-900 font-medium">{pumps.length}</p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">Setores</p>
            <p className="text-gray-900 font-medium">{project.sectors?.length || 0}</p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">Planos</p>
            <p className="text-gray-900 font-medium">{project.irrigation_plans?.length || 0}</p>
          </div>
        </div>

        {/* Capabilities badges */}
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${hasIrrigation ? "bg-blue-500" : "bg-gray-300"}`} />
            <span className={`text-xs ${hasIrrigation ? "text-blue-700" : "text-gray-400"}`}>Irrigação</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${hasFertigation ? "bg-green-600" : "bg-gray-300"}`} />
            <span className={`text-xs ${hasFertigation ? "text-green-700" : "text-gray-400"}`}>Fertirrigação</span>
          </div>
        </div>
      </div>
    </article>
  );
}

interface ProjectPumpsModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: ProjectPumpGroupData;
  onPumpClick: (pump: ConsolidatedWaterPump) => void;
}

/** Show live project pumps and sector controls with a last-open-valve confirmation. */
function ProjectPumpsModal({ isOpen, onClose, group, onPumpClick }: ProjectPumpsModalProps) {
  const [, setLocation] = useLocation();
  const { project, pumps } = group;

  const sectorsByProjectId = useAtomValue(sectorsByProjectIdAtom);
  const sectorVirtualDeviceConfigMap = useAtomValue(sectorVirtualDeviceConfigMapAtom);
  const valveStatusMap = useAtomValue(sectorValveStatusMapAtom);
  const requiresValveCloseConfirmation = useLastOpenValveCheck(pumps);
  const [pendingTurnOff, setPendingTurnOff] = useState<(() => void) | null>(null);

  const sectors = useMemo(() => sectorsByProjectId(project.id) || [], [sectorsByProjectId, project.id]);

  /** Defer a potentially unsafe valve command until the user confirms it. */
  const handleCloseValve = (sectorId: string, turnOff: () => void) => {
    if (requiresValveCloseConfirmation(sectorId)) {
      setPendingTurnOff(() => turnOff);
      return;
    }
    turnOff();
  };

  /** Sectors that have a configured valve controller */
  const controllableSectors = useMemo(() => {
    return sectors.flatMap((sector) => {
      const config = sectorVirtualDeviceConfigMap.get(sector.id);
      return config ? [{ sector, controllerId: config.device.id }] : [];
    });
  }, [sectors, sectorVirtualDeviceConfigMap]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={project.name} size="md">
      <div className="space-y-4">
        {/* Project summary */}
        <div className="bg-green-50 rounded-lg p-3 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            <span className="font-medium">{sectors.length}</span> setores ·{" "}
            <span className="font-medium">{project.irrigation_plans?.length || 0}</span> planos
          </div>
          <button
            type="button"
            className="text-xs text-green-700 font-medium hover:text-green-900 flex items-center gap-1 transition-colors"
            onClick={() => {
              onClose();
              setLocation(`/app/projects/${project.id}`);
            }}
          >
            Ver projeto
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Pumps list */}
        {pumps.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Bombas deste projeto</h4>
            {pumps.map((pump) => (
              <WaterPumpCard
                key={pump.id}
                pump={pump}
                onClick={() => {
                  onClose();
                  onPumpClick(pump);
                }}
              />
            ))}
          </div>
        )}

        {/* Sector valve controls */}
        {controllableSectors.length > 0 && (
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium text-gray-700">Controle de Setores</h4>
              <p className="text-xs text-gray-500">Acionamento manual das válvulas dos setores.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {controllableSectors.map(({ sector, controllerId }) => {
                const valveStatus: "on" | "off" = valveStatusMap.get(sector.id) || "off";
                return (
                  <DeviceActivationPanel
                    key={sector.id}
                    elementType="valve"
                    elementId={sector.id}
                    elementControllerId={controllerId}
                    turnOnDurationMinutes={1}
                    title={sector.name}
                    status={valveStatus}
                    onTurnOffRequest={(turnOff) => handleCloseValve(sector.id, turnOff)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state — no pumps AND no controllable sectors */}
        {pumps.length === 0 && controllableSectors.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
            <Droplets className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500 mb-1">Nenhuma bomba ou setor configurado</p>
            <p className="text-xs text-gray-400">
              Este projeto não possui bombas nem setores com controlador de válvula configurado.
            </p>
          </div>
        )}
      </div>
      <Modal
        isOpen={pendingTurnOff !== null}
        onClose={() => setPendingTurnOff(null)}
        title="Atenção – Uma válvula aberta"
        size="sm"
      >
        <div className="space-y-4">
          <p>Fechar a válvula com a bomba ligada pode causar danos à tubulação. Tem certeza que deseja continuar?</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPendingTurnOff(null)}>
              Cancelar
            </Button>
            <Button
              variant="warning"
              onClick={() => {
                pendingTurnOff?.();
                setPendingTurnOff(null);
              }}
            >
              Desligar
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
}

function WaterPumpPage() {
  const setBackButton = useSetAtom(appShell.backButtonAtom);
  const [selectedPumpId, setSelectedPumpId] = useState<string | null>(null);
  const [hideSectorsInModal, setHideSectorsInModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  useEffect(() => {
    setBackButton(false);
  }, [setBackButton]);

  const waterPumps = useAtomValue(waterPumpsAtom);
  const propertyDevices = useAtomValue(propertyDevicesAtom);
  const equipmentsState = useAtomValue(equipmentsStateAtom);
  const createDeviceMessageRequest = useSetAtom(createDeviceMessageRequestAtom);
  const deviceToLicMap = useAtomValue(deviceToLicMapAtom);
  const waterPumpVirtualDeviceConfigMap = useAtomValue(waterPumpVirtualDeviceConfigMapAtom);
  const { showWarning, showInfo } = useToast();

  const consolidatedData = useMemo((): ConsolidatedWaterPump[] => {
    return waterPumps.map((pump) => {
      const device = waterPumpVirtualDeviceConfigMap.get(pump.id)?.device;
      const controllerDisplayInfo = resolveDeviceDisplayInfo(device?.id ?? null, propertyDevices);
      const state = equipmentsState[pump.id];
      const status = state?.outputOn;
      return {
        ...pump,
        water_pump_controller: device,
        water_pump_controller_display_info: controllerDisplayInfo,
        current_state: {
          start_time: null,
          status: status == null ? null : status ? "on" : "off",
          time_left_minutes: null,
        },
        last_activity: "", //randomPumpState.start_time ?? genRandomDate(3), // Simulating last activity
      };
    });
  }, [waterPumps, waterPumpVirtualDeviceConfigMap, equipmentsState, propertyDevices]);

  const projects = useAtomValue(projectsAtom);

  /** Groups consolidated pumps by project, and identifies orphan pumps */
  const { projectGroups, orphanPumps } = useMemo(() => {
    const pumpIdToProjectIds = new Map<string, Set<string>>();

    // Build a set of project IDs each pump belongs to
    for (const project of projects) {
      if (project.irrigation_water_pump) {
        const ids = pumpIdToProjectIds.get(project.irrigation_water_pump) ?? new Set();
        ids.add(project.id);
        pumpIdToProjectIds.set(project.irrigation_water_pump, ids);
      }
      if (project.fertigation_water_pump) {
        const ids = pumpIdToProjectIds.get(project.fertigation_water_pump) ?? new Set();
        ids.add(project.id);
        pumpIdToProjectIds.set(project.fertigation_water_pump, ids);
      }
    }

    // Group pumps under each project
    const groups: ProjectPumpGroupData[] = projects.map((project) => {
      const pumpsForProject = consolidatedData.filter((pump) => {
        const projectIds = pumpIdToProjectIds.get(pump.id);
        return projectIds?.has(project.id) ?? false;
      });
      return { project, pumps: pumpsForProject };
    });

    // Identify orphan pumps (not associated with any project)
    const allAssociatedPumpIds = new Set(pumpIdToProjectIds.keys());
    const orphans = consolidatedData.filter((pump) => !allAssociatedPumpIds.has(pump.id));

    return { projectGroups: groups, orphanPumps: orphans };
  }, [projects, consolidatedData]);

  const selectedPump = consolidatedData.find((pump) => pump.id === selectedPumpId);
  const selectedProjectGroup = projectGroups.find((group) => group.project.id === selectedProjectId);

  const handlePumpAction = async (action: "turn_on" | "turn_off", duration?: number) => {
    if (!selectedPump?.water_pump_controller) return;
    const elementType = "pump";
    const elementId = selectedPump.id;
    const elementVariation = undefined;
    const turnOn = action === "turn_on";
    const turnOnDurationMinutes = duration ?? 1;
    const lic = deviceToLicMap.get(selectedPump.water_pump_controller.id);
    if (!lic) {
      showWarning({
        message: "Controlador de Irrigação não encontrado para o controlador de bomba.",
      });
      return;
    }

    await createDeviceMessageRequest({
      device: lic,
      payload_type: "control",
      payload_data: {
        elementType,
        elementId,
        elementVariation,
        turnOn,
        durationMinutes: turnOn ? turnOnDurationMinutes : undefined,
      },
    });
    showInfo({
      message: `Ativação de ${elementType === "pump" ? "bomba" : "válvula"} enviada com sucesso.`,
    });
  };

  const handleOpenModal = (pump: ConsolidatedWaterPump, hideSectors = false) => {
    setSelectedPumpId(pump.id);
    setHideSectorsInModal(hideSectors);
  };

  const handleCloseModal = () => {
    setSelectedPumpId(null);
    setHideSectorsInModal(false);
  };

  const handleOpenProjectModal = (group: ProjectPumpGroupData) => {
    setSelectedProjectId(group.project.id);
  };

  const handleCloseProjectModal = () => {
    setSelectedProjectId(null);
  };

  const hasNoContent = consolidatedData.length === 0 && projects.length === 0;

  return (
    <div className="p-4 pb-20 bg-gray-50 ">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Controle</h1>

      {hasNoContent ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Droplets className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma bomba cadastrada</h3>
          <p className="text-gray-600">Cadastre bombas de água para monitorar e controlar a irrigação.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Orphan pumps — pumps not associated with any project */}
          {orphanPumps.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Bombas sem projeto</h2>
              <div className="space-y-3">
                {orphanPumps.map((pump) => (
                  <WaterPumpCard key={pump.id} pump={pump} onClick={() => handleOpenModal(pump)} />
                ))}
              </div>
            </div>
          )}

          {/* Project cards */}
          {projectGroups.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Projetos</h2>
              <div className="space-y-3">
                {projectGroups.map((group) => (
                  <ProjectCard key={group.project.id} group={group} onClick={() => handleOpenProjectModal(group)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pump Control Modal */}
      {selectedPump && (
        <WaterPumpControlModal
          isOpen={true}
          onClose={handleCloseModal}
          pump={selectedPump}
          onAction={handlePumpAction}
          projects={projects}
          hideSectors={hideSectorsInModal}
        />
      )}

      {/* Project Pumps Modal */}
      {selectedProjectGroup && (
        <ProjectPumpsModal
          isOpen={true}
          onClose={handleCloseProjectModal}
          group={selectedProjectGroup}
          onPumpClick={(pump) => handleOpenModal(pump, true)}
        />
      )}
    </div>
  );
}

interface WaterPumpCardProps {
  pump: ConsolidatedWaterPump;
  onClick: () => void;
}

function WaterPumpCard({ pump, onClick }: WaterPumpCardProps) {
  const isRunning = pump.current_state.status == null ? null : pump.current_state.status === "on";
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getStatusColor = (status: "on" | "off" | null) => {
    return status == null
      ? "bg-gray-100 text-gray-600 border-gray-200"
      : status === "on"
        ? "bg-green-100 text-green-800 border-green-200"
        : "bg-gray-100 text-gray-600 border-gray-200";
  };

  const getStatusIcon = (status: "on" | "off" | null) => {
    return status == null ? (
      <CircleQuestionMark className="w-3 h-3" />
    ) : status === "on" ? (
      <Play className="w-3 h-3" />
    ) : (
      <Square className="w-3 h-3" />
    );
  };

  const formatTimeLeft = (minutes: number | null) => {
    if (!minutes) return null;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatLastActivity = (isoString: string) => {
    return dayjs(isoString).fromNow();
  };

  const getDeviceModelShort = (model: string) => {
    switch (model) {
      case "LIC":
        return "LIC";
      case "WPD":
        return "WPD";
      case "VD":
        return "VD";
      default:
        return model;
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
      onClick={onClick}
    >
      <div className="p-4">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="font-semibold text-gray-900">{pump.label}</h3>
              <p className="text-xs text-gray-500">{pump.identifier}</p>
            </div>
          </div>

          {/* Status Badge */}
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(
              pump.current_state.status
            )}`}
          >
            {getStatusIcon(pump.current_state.status)}
            {isRunning == null ? "Desconhecido" : isRunning ? "Ligada" : "Desligada"}
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-3 text-xs">
          {/* Pump Type */}
          <div>
            <p className="text-gray-500 mb-1">Tipo</p>
            <p className="text-gray-900 font-medium">{WATER_PUMP_TYPE_LABELS[pump.pump_type] || pump.pump_type}</p>
          </div>

          {/* Device Controller */}
          <div>
            <p className="text-gray-500 mb-1">Controlador</p>
            <p className="text-gray-900 font-medium">
              {pump.water_pump_controller_display_info
                ? formatDeviceDisplayText(pump.water_pump_controller_display_info)
                : pump.water_pump_controller
                  ? getDeviceModelShort(pump.water_pump_controller.model.id)
                  : "N/A"}
            </p>
          </div>

          {/* LIC Association */}
          <div>
            <p className="text-gray-500 mb-1">LIC</p>
            <p className="text-gray-900 font-medium">
              {pump.water_pump_controller_display_info?.licInfo
                ? formatLICDisplayText(pump.water_pump_controller_display_info.licInfo)
                : "N/A"}
            </p>
          </div>

          {/* Last Activity */}
          <div>
            <p className="text-gray-500 mb-1">Última Atividade</p>
            <p className="text-gray-900 font-medium">{pump.last_activity && formatLastActivity(pump.last_activity)}</p>
          </div>

          {/* Time Left or Model */}
          <div>
            {isRunning && pump.current_state.time_left_minutes ? (
              <>
                <p className="text-green-600 mb-1">Tempo Restante</p>
                <p className="text-green-800 font-bold">{formatTimeLeft(pump.current_state.time_left_minutes)}</p>
              </>
            ) : (
              <>
                <p className="text-gray-500 mb-1">Modelo</p>
                <p className="text-gray-900 font-medium truncate">{pump.pump_model}</p>
              </>
            )}
          </div>
        </div>

        {/* Running State Banner */}
        {isRunning && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-md p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-green-800">Em Funcionamento</p>
                {pump.current_state.start_time && (
                  <p className="text-xs text-green-700">
                    Iniciada: {dayjs(pump.current_state.start_time).format("HH:mm")}
                  </p>
                )}
              </div>
              <Clock className="w-3 h-3 text-green-600" />
            </div>
          </div>
        )}

        {/* Notes */}
        {pump.notes && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-xs text-blue-700 line-clamp-2">{pump.notes}</p>
          </div>
        )}
      </div>

      {/* Water Pump Control Modal */}
      <WaterPumpControlModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        pump={pump}
        onAction={async (action, duration) => {
          // Handle turn on/off action
          console.log(`Action: ${action}, Duration: ${duration}`);
          // Here you would typically call an API to perform the action
          // await api.turnOnPump(pump.id, duration);
          // or
          // await api.turnOffPump(pump.id);
        }}
      />
    </div>
  );
}

export default WaterPumpPage;
